/**
 * ReferentialPickerActions — footer cmdk partagé entre les pickers de
 * référentiel (Profession, NirRefusalReason).
 *
 * Affiche le bouton « Créer "X" » conditionnel et le bouton « Effacer la
 * sélection » côte à côte sous une bordure top. Aucune logique métier ici :
 * uniquement le rendu + i18n.
 */
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'

interface ReferentialPickerActionsProps {
    showCreateButton: boolean
    showClearButton: boolean
    onCreate: () => void
    onClear: () => void
    isCreating: boolean
    trimmedSearch: string
    createLabelKey: string
    creatingLabelKey: string
    clearLabelKey: string
    createLabelFallback: string
    creatingLabelFallback: string
    clearLabelFallback: string
}

export function ReferentialPickerActions({
    showCreateButton,
    showClearButton,
    onCreate,
    onClear,
    isCreating,
    trimmedSearch,
    createLabelKey,
    creatingLabelKey,
    clearLabelKey,
    createLabelFallback,
    creatingLabelFallback,
    clearLabelFallback,
}: ReferentialPickerActionsProps) {
    const { t } = useTranslation()
    if (!showCreateButton && !showClearButton) return null
    return (
        <div className="border-t p-1 flex flex-col gap-1">
            {showCreateButton && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-primary hover:text-primary"
                    // ⚠️ `onMouseDown` ne fait QUE retenir le focus (sans lui, le
                    // `blur` du champ de recherche fermerait le panneau avant le
                    // clic) ; l'action vit sur `onClick`. Posée sur `mousedown`,
                    // elle était injoignable au CLAVIER : Entrée et Espace sur un
                    // `<button>` émettent `click`, jamais `mousedown` (revue du
                    // 2026-09-16, sonde : 0 appel au clavier, 1 à la souris).
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void onCreate()}
                    disabled={isCreating}
                >
                    <Plus className="mr-2 h-4 w-4" />
                    {isCreating
                        ? t(creatingLabelKey, creatingLabelFallback)
                        : t(createLabelKey, createLabelFallback, {
                              name: trimmedSearch,
                          })}
                </Button>
            )}
            {showClearButton && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-muted-foreground"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onClear}
                >
                    {t(clearLabelKey, clearLabelFallback)}
                </Button>
            )}
        </div>
    )
}
